import {Component, HostListener, OnDestroy, OnInit, TemplateRef, ViewChild} from '@angular/core';
import {TranslateService} from '@ngx-translate/core';
import {RouteService} from '../../../../services/route/route.service';
import {SnackService} from '../../../../services/snack/snack.service';
import {CountryService} from '../../../../services/country/country.service';
import {cloneDeep, find, isEqual} from 'lodash-es';
import {
  ActiveStatus,
  AuditLogPage,
  EventLocationType,
  EventVenueContactPerson,
  LegalPersonList,
  VenueGet,
  VenuePost,
  VenuePut,
  VenueType
} from '../../../../generated/sales-terminal-client';
import {ValidationService} from '../../../../services/validation/validation.service';
import {AuthService} from '../../../../services/auth/auth.service';
import {GeocodingLocation} from '../../../../components/google/geocoding/geocodingLocation';
import {UtilService} from '../../../../services/util/util.service';
import {Privileges} from '../../../admin/privileges/privileges.constants';
import {SelfServiceVenueService} from '../../../../services/self-service/venue/self-service-venue.service';
import {S3UploaderRatios, S3UploaderTypes} from '../../../../components/s3/uploader/s3-uploader.types';
import {BehaviorSubject, Observable, Subscription, tap} from 'rxjs';
import {PendingChangesComponent} from "../../../../guards/pendingChanges/pending-changes-component";
import {EditorType, LanguageField} from "../../../../components/lang-tabs/lang-tabs.component";
import {PendingChangesDialogComponent} from "../../../../components/pending-changes-dialog/pending-changes-dialog.component";
import {CONTINUE_WITHOUT_SAVING} from "../../../sales-terminal/sales-terminal.constants";
import {
  CONTACT_PERSON_NAME_MAX_LENGTH,
  LegalPersonContentDialog
} from '../../../../components/legal-person-view/legal-person-view.component';
import {MatDialog} from "@angular/material/dialog";
import {ScrollLoad} from "../../../../components/scroll-load-component/ScrollLoad";
import {LegalPersonService} from "../../../../services/legalPerson/legalPerson.service";
import {StorageService} from "../../../../services/storage/storage.service";
import {TextInputType} from '../../../../components/forms/text-input/text-input.component';
import {ViewService} from '../../../../services/view/view.service';

export const VENUE_NAME_MAX_LENGTH: number = 300;
export const VENUE_WEBSITE_MAX_LENGTH: number = 70;
export const VENUE_DESCRIPTION_MAX_LENGTH: number = 10000;

@Component({
  selector: 'app-venues-view',
  templateUrl: './venues-view.component.html',
  styleUrls: ['./venues-view.component.scss']
})
export class VenuesViewComponent implements OnInit, OnDestroy, PendingChangesComponent {
  @HostListener('window:beforeunload')
  canDeactivate(): Observable<boolean> | boolean {
    return !this.isModelChanged;
  }
  @ViewChild('modalTemplate')
  modalTemplate: TemplateRef<any>;
  @ViewChild('contactPersonModalContent')
  contactPersonModalContent: TemplateRef<any>;

  public isLoading: boolean;
  public venue: VenueGet = null;
  public venueOrig: VenueGet = null;
  public isActive: boolean;
  public formattedAddress: string;
  public venueNameError: string;
  public venueNameChanged: boolean;
  public addressCleared: string;
  public fields: LanguageField[] = [];
  private _subs: Subscription[] = [];
  public locationChanged = new BehaviorSubject<boolean>(null);
  public selectedLanguages: string[] = [];
  public legalPersonScrollLoad: ScrollLoad<LegalPersonList> = this.legalPersonService.getScrollLoad();
  public auditLogPage: AuditLogPage;
  public auditPageNumber: number = 1;
  private auditPageSize: number = 30;
  public venueTypes: VenueType[] = [VenueType.Public, VenueType.Private];
  private DESCRIPTIONS_FIELD_INDEX = 0;

  constructor(public routeService: RouteService,
              private snackService: SnackService,
              private venueService: SelfServiceVenueService,
              private translateService: TranslateService,
              public authService: AuthService,
              public countryService: CountryService,
              public validationService: ValidationService,
              public utilService: UtilService,
              public dialog: MatDialog,
              public legalPersonService: LegalPersonService,
              public storageService: StorageService,
              private viewService: ViewService) {
    this._subs.push(this.authService.pendingChangeListener.subscribe(() =>
      this.authService.pendingChangesActive = this.isModelChanged));
  }

  ngOnInit(): void {
    this.storageService.set(UtilService.STORAGE_CONTENT_SCROLL, 0);
    this.venue = {
      descriptions: [],
      type: VenueType.Public
    } as VenueGet;
    this.addDefaultLanguage();
    this.fields = [
      {
        name: 'descriptions', label: 'general.api_venues_form_description', type: EditorType.TextArea,
        maxLength: VENUE_DESCRIPTION_MAX_LENGTH, required: () => false,
        disabled: this.isView,
        translations: this.venue.descriptions
      }
    ];
    this.isActive = true;
    if (!this.isCreate) {
      this.get(this.routeService.params['id']);
    }
    this._subs.push(this.countryService.loaded.subscribe(() => {
      this.updateFormattedAddress();
    }));
    if (this.isCreate) {
      this.syncModels();
      if (!this.authService.hasPublicVenuesAdminPrivilege) {
        this.venue.type = VenueType.Private;
      }
    }
  }

  ngOnDestroy(): void {
    this._subs.forEach(sub => sub.unsubscribe());
    this._subs = [];
  }

  get isCreate(): boolean {
    return this.routeService.checkRoute(['back-office', 'venues', 'create']);
  }
  get isCopy(): boolean {
    return this.routeService.checkRoute(['back-office', 'venues', 'copy']);
  }
  get isEdit(): boolean {
    return this.routeService.checkRoute(['back-office', 'venues', 'edit']);
  }
  get isView(): boolean {
    return this.routeService.checkRoute(['back-office', 'venues', 'view']);
  }

  get isModelChanged(): boolean {
    return !isEqual(this.cleanModel, this.venueOrig);
  }

  get cleanModel(): VenueGet {
    const clone = cloneDeep(this.venue);
    clone.descriptions = clone.descriptions.filter(item => !!item.value);
    return clone;
  }

  public get getDialogTitle(): string {
    if (this.isCreate) {
      return 'general.api_venues_form_create_title';
    } else if (this.isEdit) {
      return 'general.api_venues_form_edit_title';
    } else if (this.isCopy) {
      return 'general.api_venues_form_copy_title';
    }
    return this.venue.name;
  }

  public get isNotEmptyVenue(): boolean {
    return !!this.venue.placeId || !!this.venue.address || !!this.venue.countryCode || !!this.venue.county || !!this.venue.city;
  }

  openDialog(options: LegalPersonContentDialog): void {
    const origModel = cloneDeep(options.model);
    const dialogRef = this.dialog.open(this.modalTemplate, {
      width: options.width || '60%',
      data: {
        template: options.template,
        title: options.title,
        titleTooltip: options.titleTooltip,
        saveButton: options.saveButton,
        model: options.model,
        index: options.index,
        isValid: options.isValid,
        validations: options.validations,
        primaryAction: options.primaryAction,
        primaryButton: options.primaryButton,
        secondaryAction: options.secondaryAction,
        secondaryButton: options.secondaryButton,
        secondaryButtonDisabled: options.secondaryButtonDisabled,
        removeAction: options.removeAction,
        isLoading: options.isLoading,
        closeModal: null,
        additionalAction: options.additionalAction,
        nameMaxLength: options.nameMaxLength
      },
      disableClose: true
    });
    const confirmDialog = () => {
      dialogRef._containerInstance._config.disableClose = false;
      if (dialogRef._containerInstance._config.data.isValid && !isEqual(origModel, dialogRef._containerInstance._config.data.model)) {
        const pendingChanges = new PendingChangesDialogComponent(this.dialog, this.translateService, {
          pendingChangesDescription: this.pendingChangesDescription,
          buttonOk: options.saveButton && this.translateService.instant(options.saveButton) || '',
          canNotSave: options.isValid && !options.isValid()
        });
        pendingChanges.dialogRef.afterClosed().subscribe((action) => {
          if (action === CONTINUE_WITHOUT_SAVING) {
            dialogRef.close();
          } else if (action) {
            dialogRef.close(dialogRef._containerInstance._config.data.model);
          }
        });
      } else {
        dialogRef.close();
      }
    };
    dialogRef.afterOpened().subscribe(() => {
      dialogRef._containerInstance._config.data.closeModal = (res: any) => {
        if (!res) {
          confirmDialog();
        } else {
          dialogRef._containerInstance._config.disableClose = false;
          dialogRef.close(res);
        }
      };
    });
    dialogRef.backdropClick().subscribe(() => confirmDialog());
    dialogRef.afterClosed().subscribe((res) => options.callback && options.callback(res));
  }

  openContactPersonDialog(index: number, edit?: boolean): void {
    const scope: EventVenueContactPerson[] = this.venue.contactPersons;
    const model: EventVenueContactPerson = cloneDeep(scope[index]);
    const callback = (res: any) => {
      if (res) {
        scope[index] = res;
      } else if (!model.name) {
        this.spliceContactPerson(index);
      }
      this.viewService.triggerCdr();
    };
    const isEmailValidForSave = () => {
      return model.email && ValidationService.validateEmail(model.email);
    };
    const isEmailValid = () => {
      return !model.email || ValidationService.validateEmail(model.email);
    };
    const isEmailExist = () => {
      return model.email && !!scope.filter((cp, cpIndex) => cpIndex !== index && cp.email === model.email).length;
    };
    const isValid = () => {
      return (model.name && ValidationService.validateString(model.name, 2, CONTACT_PERSON_NAME_MAX_LENGTH) &&
        (!model.phoneNr || ValidationService.validatePhone(model.phoneNr))
        && isEmailValidForSave() && !isEmailExist()
      );
    };
    this.openDialog({
      template: this.contactPersonModalContent,
      callback: callback,
      isValid: isValid,
      validations: {isEmailValid, isEmailExist},
      title: edit ? 'general.api_venues_form_contact_person_edit' : 'general.api_venues_form_contact_person_add',
      saveButton: edit ? 'general.api_venues_form_contact_person_edit_button' : 'general.api_venues_form_contact_person_add_button',
      model: model,
      index: index,
      nameMaxLength: CONTACT_PERSON_NAME_MAX_LENGTH
    } as LegalPersonContentDialog);
  }

  spliceContactPerson(index: number): void {
    this.venue.contactPersons.splice(index, 1);
  }

  public get canSeeAuditLogs(): boolean {
    return this.authService.hasAuditGetPrivilege && this.authService.hasPublicVenuesAdminPrivilege
      && (this.venue.type === 'PUBLIC' || (this.venue.type === 'PRIVATE' && this.authService.user.centreId === this.venue.ownerCentreId));
  }

  public getAuditLogs(): void {
    if (this.canSeeAuditLogs) {
      this.venueService.getAuditLogs(this.venue.id, this.auditPageNumber, this.auditPageSize)
        .subscribe((auditPage: AuditLogPage) => {
        this.auditLogPage = auditPage;
      });
    }
  }

  get isValid(): boolean {
    const name = ValidationService.validateString(this.venue.name, 3, CONTACT_PERSON_NAME_MAX_LENGTH);
    const city = !!this.venue.city;
    const country = !!this.venue.countryCode;
    const capacity = this.validationService.emptyOrNumeric(this.venue.maxCapacity);
    const website = !this.venue.website || this.isWebsiteLinkValid;
    const contactPersons = (this.venue.contactPersons || []).filter(cp => {
      const name = ValidationService.validateString(cp.name, 3, CONTACT_PERSON_NAME_MAX_LENGTH);
      const email = ValidationService.validateEmail(cp.email)
      const phone = !cp.phoneNr || ValidationService.validatePhone(cp.phoneNr);
      return !name || !email || !phone;
    }).length;
    return name && city && country && capacity && website && !contactPersons;
  }

  private get(id: number): void {
    this.isLoading = true;
    this.venueService.get(id).pipe(
      tap({finalize: () => this.isLoading = false})
    ).subscribe((response) => {
      this.venue = response;
      this.getAuditLogs();
      this.venueNameChanged = true;
      this.updateFormattedAddress();
      this.isActive = this.venue.status === ActiveStatus.Active;
      this.selectedLanguages = this.venue.descriptions.map(e => e.language);
      if (!this.selectedLanguages.includes(this.authService.salesPointLanguage)) {
        this.addDefaultLanguage();
      }
      this.fields[this.DESCRIPTIONS_FIELD_INDEX].translations.forEach(translation => {
        let description = this.venue.descriptions.find(vd => vd.language === translation.language);
        if (description) {
          translation.value = description.value;
        }
      });
      if (this.venue.ownerLegalPersonId) {
        this.legalPersonService.get(this.venue.ownerLegalPersonId).subscribe((legal) => {
          const scrollLoadObject: LegalPersonList = cloneDeep(legal) as LegalPersonList;
          scrollLoadObject.countryCode = legal.address.countryCode;
          this.legalPersonScrollLoad.object = scrollLoadObject;
        });
      }
      setTimeout(() => {
        this.syncModels();
      }, 0);
    });
  }

  syncVenueDescriptions() {
    this.venue.descriptions = this.fields[this.DESCRIPTIONS_FIELD_INDEX].translations
      .filter(translation => this.selectedLanguages.find(sl => sl === translation.language));
  }

  viewMode(): void {
    this.routeService.replaceUrl('/app/back-office/venues/view/' + this.venue.id);
  }
  editMode(): void {
    this.routeService.replaceUrl('/app/back-office/venues/edit/' + this.venue.id);
  }
  copy(): void {
    this.routeService.navigate('/app/back-office/venues/copy/' + this.venue.id);
  }

  updateFormattedAddress(): void {
    this.formattedAddress = this.venueService.formatAddressForMap(this.venue);
  }

  updateLocationChangeState(LocationChanged: any) {
    this.locationChanged.next(LocationChanged);
  }

  syncModels(): void {
    this.venueOrig = this.cleanModel;
  }

  create(closeOnSuccess?: boolean, doAuthAction?: boolean): void {
    const createObject: VenuePost = this.getPostData();
    this.venueService.create(createObject).pipe(
      tap({error: (e) => {
        if (e?.error && e.error.message === 'api_public_venue_already_exist') {
          this.venueNameError = this.translateService.instant('general.api_venues_validation_name_and_address_should_be_unique');
        }
      }})
    ).subscribe((response) => {
      this.venue = response;
      this.updateFormattedAddress();
      this.isActive = this.venue.status === ActiveStatus.Active;
      this.syncModels();
      delete this.venueNameError;
      this.snackService.showSuccess(this.translateService.instant('general.api_venues_successfully_created'));
      if (doAuthAction) {
        this.authService.pendingChangeAction();
      } else if (!closeOnSuccess) {
        this.get(this.venue.id);
        this.viewMode();
      }
    });
  }
  public hasVenueNameError(): boolean {
    return !!this.venueNameError?.length;
  }

  update(closeOnSuccess?: boolean, doAuthAction?: boolean): void {
    this.venueService.update(this.venue.id, this.getPutData()).pipe(
      tap({error: (e) => {
        if (e?.error && e.error.message === 'api_public_venue_already_exist') {
          this.venueNameError = this.translateService.instant('general.api_venues_validation_name_and_address_should_be_unique');
        }
      }})
    ).subscribe((response) => {
      this.venue = response;
      this.updateFormattedAddress();
      this.isActive = this.venue.status === ActiveStatus.Active;
      this.syncModels();
      delete this.venueNameError;
      this.snackService.showSuccess(this.translateService.instant('general.api_venues_successfully_updated'));
      if (doAuthAction) {
        this.authService.pendingChangeAction();
      } else if (!closeOnSuccess) {
        this.get(this.venue.id);
        this.viewMode();
      }
    });
  }

  private getPostData(): VenuePost {
    const data = cloneDeep(this.venue);
    this.prepareCommonData(data);
    return data as VenuePost;
  }
  private getPutData(): VenuePut {
    const data = cloneDeep(this.venue);
    delete data.id;
    this.prepareCommonData(data);
    return data as VenuePut;
  }
  private prepareCommonData(data: VenueGet): void {
    data.status = this.isActive ? ActiveStatus.Active : ActiveStatus.Inactive;
    data.maxCapacity = data.maxCapacity == null ? null : Number(data.maxCapacity);
    data.descriptions = data.descriptions.filter(description => description.value?.length);
  }

  addContactPerson(): void {
    if (!this.venue.contactPersons) {
      this.venue.contactPersons = [];
    }
    const count = this.venue.contactPersons.push({
      name: null,
      email: null,
      phoneNr: null
    });
    this.openContactPersonDialog(count - 1);
  }

  editContactPerson(idx: number): void {
    this.openContactPersonDialog(idx, true);
  }

  removeContactPerson(idx: number): void {
    this.venue.contactPersons.splice(idx, 1);
  }

  fillLocationData(geoLocation: GeocodingLocation, updateAddress?: boolean): void {
    if (!this.venueNameChanged) {
      this.venue.name = geoLocation.name || '';
    }
    this.venue.address = geoLocation.location || '';
    this.venue.county = geoLocation.county || '';
    this.venue.city = geoLocation.city || '';
    this.venue.countryCode = '';
    if (geoLocation.countryCode) {
      const result = find(this.countryService.countries, {code: geoLocation.countryCode});
      if (result) {
        this.venue.countryCode = result.code;
      }
    }
    this.venue.website = geoLocation.website || '';
    this.venue.latitude = geoLocation?.latitude;
    this.venue.longitude = geoLocation?.longitude;
    this.venue.placeId = geoLocation?.placeId;
    if (updateAddress) {
      this.updateFormattedAddress();
    }
    this.updateLocationChangeState(true);
  }

  public clearVenue(): void {
    this.fillLocationData({} as GeocodingLocation, true);
    this.addressCleared = new Date().toDateString();
  }

  get buttonOk(): string {
    let text: string = undefined;
    if (this.isCreate) {
      text = this.translateService.instant('general.api_venues_form_create');
    }
    if (this.isCopy) {
      text = this.translateService.instant('general.api_venues_form_copy');
    }
    return text;
  }

  get pendingChangesDescription(): string {
    return this.translateService.instant('general.api_venues_description_unsaved_changes');
  }

  continueWithoutSaving(): void {
    this.syncModels();
  }

  saveAndContinue(doAuthAction?: boolean): void {
    if (this.isCreate || this.isCopy) {
      this.create(true, doAuthAction);
    } else {
      this.update(true, doAuthAction);
    }
  }

  private addDefaultLanguage():void{
    this.selectedLanguages.push(this.authService.salesPointLanguage);
  }

  public get isWebsiteLinkValid(): boolean {
    return ValidationService.validateUrl(this.venue?.website) && this.venue?.website?.length <= VENUE_WEBSITE_MAX_LENGTH;
  }

  backToList(): void {
    this.routeService.replaceUrl('/app/back-office/venues');
  }

  isPrivateVenueOwner(selectedVenue: VenueGet): boolean {
    return this.authService.user.salespointId === selectedVenue?.ownerCentreId && selectedVenue?.type === VenueType.Private
  }

  protected readonly EventLocationType = EventLocationType;
  protected readonly Privileges = Privileges;
  protected readonly S3UploaderRatios = S3UploaderRatios;
  protected readonly S3UploaderTypes = S3UploaderTypes;
  protected readonly TextInputType = TextInputType;
  protected readonly VenueType = VenueType;
  protected readonly VENUE_NAME_MAX_LENGTH = VENUE_NAME_MAX_LENGTH;
  protected readonly VENUE_WEBSITE_MAX_LENGTH = VENUE_WEBSITE_MAX_LENGTH;
}
